/* APPROVAL — the Reviewer's screen (2026-10-15; called "Cash Report Approval" until 2026-10-16, when
   Tuan asked for the menu to read simply "Approval": Submit is the Preparer's tab, Approval the
   Reviewer's, Cash Report what everybody reads once the Reviewer has agreed). The Group CFO and the
   CEO took part here until the same day; the flow is now Preparer → Reviewer and nothing after.

   Max: "1 module hay 1 cái screen riêng dành cho các role khác để duyệt phiếu từ prepare á chứ ko
   gộp vào màn hình của 3 cái report". The split the portal already keeps twice — Project Tracker /
   Project Approval, GTM Programmes / GTM Approval: the Preparer enters and sends in Submit Cash
   Report, the people who decide do it here, and the three report screens are for reading.

   ONE QUEUE ACROSS THE THREE REPORTS AND EVERY PERIOD. A Reviewer used to have to know which report
   screen, which week and which entity before the thing to approve was even on the screen; here the
   first tab is exactly what waits on them, oldest first. Whether a row waits on the reader is the
   server's `actionsFor` — the rule the action endpoint enforces — so the tab and the buttons cannot
   disagree. The decision itself is taken in the same panel and the same dialog as before
   (CashReportPanel, mode "approve"), so nothing about HOW a report is decided changed, only where.

   The selection lives in the URL (?type=&period=&entity=), which is what the mails link to. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Compass, Loader2 } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import CashReportPanel from './CashReportPanel';
import { cashTypeOf } from './cashTypes';
import { API, call, fmtStamp, StateChip, TestAsBar } from './cashUi';
import { SortTh } from './15_SampleTracker';
import useColumnTools from './useColumnTools';
import TourGuide from './TourGuide';
import { ManualButton, MANUAL_STEP } from './ModuleHelp';
import './SPHub.css';
import './ProjectApproval.css';
import './CashSetup.css';
import './CashWeekly.css';

/* Three tabs, in the order somebody works them: what is mine to decide, what is still moving, what
   is done. A report SENT BACK is still moving — back a step, with the Preparer — so it sits in In
   progress with its own chip and reason, not in a tab of its own (Max, 2026-10-15: "nếu bị trả lại
   thì nó sẽ nhảy vào in progress … chứ ko cần tạo mới 1 tab á"). */
const TABS = [
  { key: 'waiting', test: (r) => r.waiting },
  { key: 'progress', test: (r) => !r.waiting && ['submitted', 'returned'].includes(r.status) },
  /* Agreed: the Reviewer's agreement is the last step since 2026-10-16 (Max: "CFO và CEO thì ko tham
     gia vào quá trình duyệt nhé"), so a reviewed report is DONE, and it is what the Cash Report
     shows. The key stays `closed` — it is in links already sent; a report that went further under
     the old four-step flow is done as well. */
  { key: 'closed', test: (r) => !r.waiting && ['reviewed', 'approved', 'closed'].includes(r.status) },
];
const keyOf = (r) => `${r.type}|${r.entity}|${r.period}`;

/* THE GUIDE — Max, 2026-10-15: "thêm guide cho page này nhé", the same TourGuide as Submit Cash
   Report and the Sample Tracker. Ordered the way a decider works: which tab, which report, where it
   stands, the one button that is theirs, then what they decide it on — the Data Check, the figures,
   the statements, the history. A step whose control is not on the page is skipped, so the decision
   step falls away for a report that is not the reader's to decide. */
const TOUR_STEPS = [
  { selector: '[data-tour="cq-tabs"]', title: 'tour.cqTabsT', body: 'tour.cqTabsB' },
  { selector: '[data-tour="cq-list"]', title: 'tour.cqListT', body: 'tour.cqListB' },
  { selector: '.cq-panel .cw-steps', title: 'tour.ciStepsT', body: 'tour.ciStepsB' },
  { selector: '.cq-panel .cw-acts', title: 'tour.cqDecideT', body: 'tour.cqDecideB' },
  { selector: '.cq-panel .cs-checkbar', title: 'tour.cqCheckT', body: 'tour.cqCheckB' },
  { selector: '.cq-panel [data-tour="report-body"]', title: 'tour.cqBodyT', body: 'tour.cqBodyB' },
  { selector: '.cq-panel [data-tour="ci-files"]', title: 'tour.cqFilesT', body: 'tour.cqFilesB' },
  { selector: '.cq-panel [data-tour="ci-history"]', title: 'tour.cqHistoryT', body: 'tour.cqHistoryB' },
  { selector: '.cw-testas', title: 'tour.ciTestAsT', body: 'tour.ciTestAsB' },
  MANUAL_STEP,
];
const TOUR_SEEN = 'portalCashApprovalTourSeen';
const day = (v) => (v ? String(v).slice(0, 10) : '');

export default function CashApproval() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const j = await call(`${API}/approval`);
      if (mine !== seq.current) return;
      setData(j); setError('');
    } catch (e) { if (mine === seq.current) setError(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sel = { type: params.get('type') || '', entity: params.get('entity') || '', period: params.get('period') || '' };
  const rows = useMemo(() => data?.rows || [], [data]);
  const selected = rows.find((r) => r.type === sel.type && r.entity === sel.entity && r.period === sel.period) || null;
  /* A link from a mail names a report and no tab: open the tab that report is in, so it is lit in
     the list as well as open below it. */
  const tabKey = params.get('tab')
    || (selected ? (TABS.find((x) => x.test(selected)) || TABS[0]).key : 'waiting');
  const tab = TABS.find((x) => x.key === tabKey) || TABS[0];
  const inTab = useMemo(() => {
    const list = rows.filter(tab.test);
    // Waiting: the longest waiting first. Everything else: the latest first.
    return tab.key === 'waiting'
      ? [...list].sort((a, b) => String(a.sentAt || '').localeCompare(String(b.sentAt || '')))
      : [...list].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')));
  }, [rows, tab]);

  const columns = useMemo(() => [
    { key: 'cashReport', get: (r) => `${cashTypeOf(r.type)?.tpl || ''} · ${r.report}` },
    { key: 'cashEntity', get: (r) => `${r.entity} · ${r.entityName}` },
    { key: 'cashPeriod', get: (r) => r.periodLabel },
    { key: 'cashStatus', get: (r) => t(`cashw.st_${r.status}`) },
    { key: 'cashSentBy', get: (r) => r.sentBy || '' },
    { key: 'cashSentAt', get: (r) => day(r.sentAt) },
    { key: 'cashUpdatedAt', get: (r) => day(r.updatedAt) },
  ], [t]);
  const tools = useColumnTools(inTab, columns);

  const setQuery = useCallback((next) => {
    setParams((cur) => {
      const p = new URLSearchParams(cur);
      Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
      return p;
    }, { replace: true });
  }, [setParams]);
  const pickTab = (key) => { if (key !== tab.key) { tools.clear(); setQuery({ tab: key, type: '', entity: '', period: '' }); } };
  /* Picking a row reloads the list too: a queue left open goes stale, and a report sent back or
     decided by somebody else since then should say so here rather than fail to open. */
  const pick = (r) => {
    if (selected && keyOf(selected) === keyOf(r)) { setQuery({ type: '', entity: '', period: '' }); return; }
    setQuery({ tab: tab.key, type: r.type, entity: r.entity, period: r.period });
    load();
  };

  // The panel opens under the list; bring it into view when a row is picked, not on every reload.
  const panelRef = useRef(null);
  const selKey = selected ? keyOf(selected) : '';
  useEffect(() => {
    // Not when the guide opened it: the guide scrolls to each step itself, and a smooth scroll still
    // running underneath would move the page out from under its first step.
    if (quietScroll.current) { quietScroll.current = false; return; }
    if (selKey && panelRef.current) panelRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [selKey]);

  /* The guide needs a report open to show the half below the list. Started with none open, it opens
     the first report there is — waiting on the reader first — and waits for the panel to land before
     it starts, because TourGuide reads which controls are on the page as it opens. */
  const [tourOpen, setTourOpen] = useState(false);
  const quietScroll = useRef(false);
  const startTour = useCallback(() => {
    const first = TABS.map((x) => [x, rows.filter(x.test)]).find(([, list]) => list.length);
    if (!selected && first) {
      const [tb, list] = first;
      quietScroll.current = true;
      setQuery({ tab: tb.key, type: list[0].type, entity: list[0].entity, period: list[0].period });
    }
    const t0 = Date.now();
    const wait = () => {
      if (document.querySelector('.cq-panel .cw-steps') || Date.now() - t0 > 6000 || (!first && !selected)) setTourOpen(true);
      else window.setTimeout(wait, 150);
    };
    wait();
  }, [rows, selected, setQuery]);
  // Once, unasked, the first time the queue has loaded — the Sample Tracker's rule: remembered when it
  // ends, by Done or Skip alike.
  const autoTour = useRef(false);
  useEffect(() => {
    if (!data || autoTour.current) return undefined;
    autoTour.current = true;
    let seen = true;
    try { seen = !!localStorage.getItem(TOUR_SEEN); } catch { seen = true; }
    if (seen) return undefined;
    const id = window.setTimeout(startTour, 600);
    return () => window.clearTimeout(id);
  }, [data, startTour]);
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_SEEN, '1'); } catch { /* nothing to remember it in */ }
  };

  const C = cashTypeOf(sel.type);
  const counts = Object.fromEntries(TABS.map((x) => [x.key, rows.filter(x.test).length]));

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main cs-page cw-page">
        <div className="ci-titlerow">
          <div className="pa-title">
            <h2>{t('cashq.title')}</h2>
            <p>{t('cashq.sub')}</p>
          </div>
          <div className="mh-bar">
            <button type="button" className="pa-btn ci-guide" title={t('st.tourReopen')} onClick={startTour} disabled={!data}>
              <Compass size={14} /> {t('st.tourButton')}
            </button>
            <ManualButton manual="cash-roles" />
          </div>
        </div>
        {data && <TestAsBar t={t} me={data.me} />}

        <div className="pa-tabs view-tabs" role="tablist" data-tour="cq-tabs">
          {TABS.map((x) => (
            <button key={x.key} type="button" role="tab" aria-selected={x.key === tab.key}
              className={`pa-tab${x.key === tab.key ? ' active' : ''}`} onClick={() => pickTab(x.key)}>
              <TabIcon k={x.key} />{t(`cashq.tab_${x.key}`)} <span className="pa-count">{counts[x.key] || 0}</span>
            </button>
          ))}
        </div>

        {error && <div className="pa-banner err">{error}</div>}
        {!error && !data && <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cash.loading')}</div>}
        {!error && data && (
          <div className="pa-section cq-list" data-tour="cq-list">
            <div className="cq-head">
              <span className="pa-muted">{t('cashq.rows', { count: tools.shown.length })}</span>
              {tools.active && <button type="button" className="pa-btn" onClick={tools.clear}>{t('cashq.clear')}</button>}
            </div>
            {inTab.length ? (
              <div className="cs-table-wrap">
                <table className="pa-table cw-table cq-table">
                  <thead>
                    <tr>
                      <SortTh label={t('cashq.colReport')} {...tools.thProps('cashReport')} />
                      <SortTh label={t('cashq.colEntity')} {...tools.thProps('cashEntity')} />
                      <SortTh label={t('cashq.colPeriod')} {...tools.thProps('cashPeriod')} />
                      <SortTh label={t('cashq.colStatus')} {...tools.thProps('cashStatus')} />
                      <SortTh label={t('cashq.colSentBy')} {...tools.thProps('cashSentBy')} />
                      <SortTh label={t('cashq.colSentAt')} {...tools.thProps('cashSentAt')} />
                      <SortTh label={t('cashq.colUpdated')} {...tools.thProps('cashUpdatedAt')} />
                    </tr>
                  </thead>
                  <tbody>
                    {tools.shown.map((r) => {
                      const on = selected && keyOf(selected) === keyOf(r);
                      return (
                        <tr key={keyOf(r)} className={`cq-row${on ? ' on' : ''}`} onClick={() => pick(r)}
                          aria-selected={on} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') pick(r); }}>
                          <td><b>{cashTypeOf(r.type)?.tpl}</b> · {r.report}</td>
                          <td><span className="mono">{r.entity}</span> <span className="pa-muted">· {r.entityName}</span></td>
                          <td>{r.periodLabel}</td>
                          <td>
                            <StateChip t={t} row={{ status: r.status, version: r.version }} />
                            {r.waiting && <span className="cw-waiting">{t('cashw.waitingYou')}</span>}
                            {r.status === 'returned' && r.returnedNote && <div className="cw-sub" title={r.returnedNote}>{r.returnedNote}</div>}
                          </td>
                          <td>{r.sentBy || <span className="pa-muted">—</span>}</td>
                          <td>{fmtStamp(r.sentAt)}</td>
                          <td>{fmtStamp(r.updatedAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : <div className="pa-empty">{t(`cashq.empty_${tab.key}`)}</div>}
          </div>
        )}

        {/* THE REPORT AND ITS DECISION — the same panel the report screens draw, in its approve mode:
            the Data Check, what was sent, the files, the history, and the one move that is the
            reader's. Reloading the list after a decision is what moves the row to its next tab. */}
        {selected && C && (
          <div ref={panelRef} className="cq-panel">
            <CashReportPanel key={keyOf(selected)} mode="approve" t={t} type={selected.type}
              entity={selected.entity} period={selected.period} onChanged={load} fileKinds={C.fileKinds}
              heading={(d) => C.heading(t, d)} renderBody={(d, h) => C.body(t, d, h)} />
          </div>
        )}
        {data && !selected && sel.type && (
          <div className="pa-empty">{t('cashq.notListed')}</div>
        )}
      </div>
      <TourGuide steps={TOUR_STEPS} open={tourOpen} onClose={closeTour} onFinish={closeTour} />
    </div>
  );
}
